// Named markets are authoritative. Older pair/DXY records are adapted on read,
// so opening an outlook never rewrites its history.
export const tradingDays = ['Monday','Tuesday','Wednesday','Thursday','Friday'];
export const calendarDays = [...tradingDays,'Saturday','Sunday'];
export const newOutlookMarket = id => ({id,name:'',bias:'',chart:null,analysis:'',keyLevels:''});
export function outlookMarkets(outlook = {}, includeInactive = false) {
  const markets = Array.isArray(outlook.markets) ? outlook.markets : [
    {...newOutlookMarket('primary'),name:outlook.pairName || 'Market',bias:outlook.pairBias || '',chart:outlook.pairChart || null,analysis:outlook.pairAnalysis || ''},
    ...([outlook.dxyBias,outlook.dxyChart,outlook.dxyAnalysis,...Object.values(outlook.dailyOutlooks || {}).map(day=>day?.dxyChart)].some(Boolean) ? [{...newOutlookMarket('dxy'),name:'DXY',bias:outlook.dxyBias || '',chart:outlook.dxyChart || null,analysis:outlook.dxyAnalysis || ''}] : [])
  ];
  const normalised=markets.map(market=>({...newOutlookMarket(market.id),...market}));
  return includeInactive ? normalised : normalised.filter(market=>market.active !== false);
}
export const marketLabel = outlook => outlookMarkets(outlook).map(market=>market.name || 'Market').join(' · ') || 'Market preparation';
export const marketHasContent = market => !!market && [market.bias,market.chart,market.analysis,market.keyLevels,market.notes].some(Boolean);
export function dailyMarkets(outlook, day, includeExcluded = false) {
  const daily = outlook?.dailyOutlooks?.[day] || {};
  const primaryId=outlookMarkets(outlook,true)[0]?.id;
  return outlookMarkets(outlook).filter(market=>includeExcluded || !(daily.excludedMarketIds || []).includes(market.id)).map(market=>({
    chart:market.id === 'dxy' ? daily.dxyChart || null : market.id === primaryId ? daily.pairChart || null : null,
    ...(daily.marketPlans?.[market.id] || {}),
    id:market.id,name:market.name || 'Market',weeklyBias:market.bias
  }));
}
export function dailyHasContent(daily, outlook, day) {
  if(!daily)return false;
  if([daily.bias,daily.sessionFocus,daily.keyLevels,daily.notes].some(Boolean))return true;
  if(outlook)return dailyMarkets(outlook,day).some(marketHasContent);
  return [daily.dxyChart,daily.pairChart,...Object.entries(daily.marketPlans || {}).filter(([id])=>!(daily.excludedMarketIds || []).includes(id)).map(([,market])=>marketHasContent(market))].some(Boolean);
}
export function outlookDays(outlook = {}) {
  return (outlook.includeWeekends ?? ['Saturday','Sunday'].some(day=>dailyHasContent(outlook.dailyOutlooks?.[day],outlook,day))) ? calendarDays : tradingDays;
}
export function serialiseOutlook(outlook) {
  const markets = outlookMarkets(outlook), primary = markets[0], dxy = markets.find(market=>market.name.trim().toUpperCase() === 'DXY');
  const dailyOutlooks = Object.fromEntries(Object.entries(outlook.dailyOutlooks || {}).map(([day,daily])=>{
    if(!daily.marketPlans)return [day,daily];
    const selected=dailyMarkets(outlook,day),p=selected.find(market=>market.id===primary?.id),d=selected.find(market=>market.id===dxy?.id);
    return [day,{...daily,pairChart:p?.chart || null,dxyChart:d?.chart || null}];
  }));
  return {...outlook,markets:outlookMarkets(outlook,true),pairName:primary?.name.trim() || '',pairBias:primary?.bias || '',pairChart:primary?.chart || null,pairAnalysis:primary?.analysis || '',dxyBias:dxy?.bias || '',dxyChart:dxy?.chart || null,dxyAnalysis:dxy?.analysis || '',dailyOutlooks};
}
export async function uploadOutlookCharts(outlook, upload) {
  const cache=new Map();
  const image=(src,slot)=>{
    if(!src)return Promise.resolve(null);
    if(src.startsWith('https://'))return Promise.resolve(src);
    if(!cache.has(src))cache.set(src,upload(src,slot));
    return cache.get(src);
  };
  const markets=await Promise.all(outlookMarkets(outlook,true).map(async market=>({...market,chart:await image(market.chart,`market_${market.id}`)})));
  const dailyOutlooks={};
  for(const [day,daily] of Object.entries(outlook.dailyOutlooks || {})) {
    const plans={...daily.marketPlans};
    // Include hidden markets so switching them back on retains their images.
    for(const market of outlookMarkets(outlook,true)) {
      const fallback=market.id==='dxy'?daily.dxyChart:market.id===markets[0]?.id?daily.pairChart:null;
      const plan=daily.marketPlans?.[market.id];
      if(plan || fallback)plans[market.id]={...(plan || {}),chart:await image(plan ? plan.chart : fallback,`daily_${day}_${market.id}`)};
    }
    dailyOutlooks[day]=Object.keys(plans).length?{...daily,marketPlans:plans}:daily;
  }
  return serialiseOutlook({...outlook,markets,dailyOutlooks,newsScreenshot:await image(outlook.newsScreenshot,'newsScreenshot')});
}
